"use client"

import { WalletReadyState } from "@solana/wallet-adapter-base"
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react"
import { Dialog } from "@src/components/Dialog"
import {
  ALWAYS_AVAILABLE_EVM_CONNECTOR_IDS,
  type ChainId,
  evmConnectorIdsForResolver,
  walletIconForConnector,
  walletsForChain,
} from "@src/config/walletRegistry"
import { ChainType, type useConnectWallet } from "@src/hooks/useConnectWallet"
import { useNearWallet } from "@src/providers/NearWalletProvider"
import { useStellarWallet } from "@src/providers/StellarWalletProvider"
import { logger } from "@src/utils/logger"
import { useCallback, useMemo, useState } from "react"
import { useConnectors } from "wagmi"
import Badge from "../Badge"
import type { ChainIcon } from "../DefuseSDK/constants/blockchains"
import Spinner from "../Spinner"
import WalletButton from "../WalletButton"
import { connectViaResolver } from "./connectViaResolver"

type SignInFn = ReturnType<typeof useConnectWallet>["signIn"]

type WalletStatus = "installed" | "available" | "not-installed"

type DisplayWallet = {
  key: string
  testId: string
  name: string
  icon?: ChainIcon | string
  status: WalletStatus
  onConnect: () => Promise<void>
}

const CHAIN_LABELS: Record<ChainId, string> = {
  near: "NEAR",
  evm: "EVM",
  solana: "Solana",
  stellar: "Stellar",
}

const STATUS_ORDER: Record<WalletStatus, number> = {
  installed: 0,
  available: 1,
  "not-installed": 2,
}

// Stellar kit modules whose `isAvailable` is hardcoded to true regardless of
// installation state (they connect via iframe bridges or SDKs, not extensions).
// For these we can't tell whether the user has anything installed, so we fall
// back to "available" — no Installed pill, no Install CTA.
const STELLAR_UNRELIABLE_AVAILABILITY: ReadonlySet<string> = new Set([
  "xbull",
  "hot-wallet",
])

// Solana adapters whose readyState is `Loadable` only because they ship a
// web-SDK fallback that displays an install prompt when the user lacks the
// extension (Solflare is the canonical case). Treat these as not-installed
// when Loadable so the user gets a clean Install CTA instead of Solflare's
// iframe pushing them to install.
const SOLANA_EXTENSION_FIRST_ADAPTERS: ReadonlySet<string> = new Set([
  "Solflare",
])

export function ChainContainerModal({
  chain,
  open,
  onClose,
  onCloseAnimationEnd,
  signIn,
}: {
  chain: ChainId | null
  open: boolean
  onClose: () => void
  onCloseAnimationEnd?: () => void
  /**
   * Pass the login page's `signIn` (from its own useConnectWallet instance)
   * so connection failures set the page-level signInError state.
   */
  signIn: SignInFn
}) {
  const wagmiConnectors = useConnectors()
  const solanaWallet = useSolanaWallet()
  const { connector: nearConnector } = useNearWallet()
  const { availableWallets: stellarWallets } = useStellarWallet()

  // While a connection is in flight, disable other tiles so a hurried
  // double-click or quick second pick can't kick off concurrent signIn calls.
  // Reset whenever the modal closes so each open starts fresh.
  const [connectingKey, setConnectingKey] = useState<string | null>(null)

  const dialogOpen = open && chain !== null

  const handleClose = useCallback(() => {
    setConnectingKey(null)
    onClose()
  }, [onClose])

  const safeConnect = useCallback(
    async (key: string, fn: () => Promise<boolean>) => {
      setConnectingKey(key)
      try {
        const connected = await fn()
        if (connected) handleClose()
      } catch (error) {
        logger.error("Wallet picker connect failed", { error, chain })
      } finally {
        setConnectingKey(null)
      }
    },
    [chain, handleClose]
  )

  const wallets = useMemo<DisplayWallet[]>(() => {
    if (chain === null) return []

    const ctx = { signIn, wagmiConnectors }
    const registryEntries = walletsForChain(chain)
    const out: DisplayWallet[] = []
    const coveredNames = new Set<string>()

    if (chain === "evm") {
      const coveredIds = new Set<string>()
      const hasBinanceProvider =
        typeof window !== "undefined" && !!window.binancew3w

      for (const entry of registryEntries) {
        const resolver = entry.resolve.evm
        if (!resolver) continue
        const candidateIds = evmConnectorIdsForResolver(resolver)
        for (const id of candidateIds) coveredIds.add(id)
        coveredNames.add(entry.name.toLowerCase())

        // Determine status + matched connector for icon fallback.
        let status: WalletStatus = "not-installed"
        let matchedId: string | undefined
        for (const id of candidateIds) {
          const c = wagmiConnectors.find((x) => x.id === id)
          if (!c) continue
          matchedId = id
          if (id === "com.binance.w3w") {
            status = hasBinanceProvider ? "installed" : "not-installed"
          } else if (ALWAYS_AVAILABLE_EVM_CONNECTOR_IDS.includes(id)) {
            status = "available"
          } else {
            status = "installed"
          }
          break
        }
        // If only the WC fallback matched but a higher-priority extension id
        // didn't, status reflects the WC fallback (status ends up "available").

        const matchedConnector = matchedId
          ? wagmiConnectors.find((c) => c.id === matchedId)
          : undefined
        const fallbackIcon = matchedConnector?.icon?.trim() || undefined

        out.push({
          key: entry.id,
          testId: `wallet-tile-${entry.id}`,
          name: entry.name,
          icon: entry.icon ?? fallbackIcon,
          status,
          onConnect: () =>
            safeConnect(entry.id, () => connectViaResolver(entry, "evm", ctx)),
        })
      }

      for (const c of wagmiConnectors) {
        if (c.id === "injected") continue
        if (coveredIds.has(c.id)) continue
        if (coveredNames.has(c.name.toLowerCase())) continue
        // EIP-6963 only announces installed wallets, so any auto-discovered
        // connector reaching this point is installed.
        out.push({
          key: `wagmi:${c.uid}`,
          testId: `wallet-tile-${c.id}`,
          name: c.name,
          icon:
            walletIconForConnector({
              chain: "evm",
              id: c.id,
              name: c.name,
            }) ??
            c.icon?.trim() ??
            undefined,
          status: "installed",
          onConnect: () =>
            safeConnect(`wagmi:${c.uid}`, () =>
              signIn({ id: ChainType.EVM, connector: c })
            ),
        })
      }
    } else if (chain === "solana") {
      const coveredAdapterNames = new Set<string>()

      for (const entry of registryEntries) {
        const resolver = entry.resolve.solana
        if (resolver?.kind !== "solana-adapter") continue
        const adapter = solanaWallet.wallets.find(
          (w) => w.adapter.name === resolver.adapterName
        )
        if (!adapter) continue
        if (adapter.adapter.readyState === WalletReadyState.Unsupported) {
          continue
        }
        coveredAdapterNames.add(resolver.adapterName)
        coveredNames.add(entry.name.toLowerCase())
        out.push({
          key: entry.id,
          testId: `wallet-tile-${entry.id}`,
          name: entry.name,
          icon: entry.icon ?? adapter.adapter.icon ?? undefined,
          status: solanaReadyStateToStatus(
            adapter.adapter.readyState,
            adapter.adapter.name
          ),
          onConnect: () =>
            safeConnect(entry.id, () =>
              connectViaResolver(entry, "solana", ctx)
            ),
        })
      }

      for (const w of solanaWallet.wallets) {
        const adapterName = w.adapter.name
        if (coveredAdapterNames.has(adapterName)) continue
        if (coveredNames.has(adapterName.toLowerCase())) continue
        if (w.adapter.readyState === WalletReadyState.Unsupported) continue
        coveredAdapterNames.add(adapterName)
        coveredNames.add(adapterName.toLowerCase())
        out.push({
          key: `solana:${adapterName}`,
          testId: `wallet-tile-solana-${adapterName}`,
          name: adapterName,
          icon:
            walletIconForConnector({
              chain: "solana",
              id: adapterName,
              name: adapterName,
            }) ??
            w.adapter.icon ??
            undefined,
          status: solanaReadyStateToStatus(w.adapter.readyState, adapterName),
          onConnect: () =>
            safeConnect(`solana:${adapterName}`, () =>
              signIn({ id: ChainType.Solana, adapterName })
            ),
        })
      }
    } else if (chain === "near") {
      const coveredWalletIds = new Set<string>()
      const nearWallets = nearConnector?.availableWallets ?? []

      for (const entry of registryEntries) {
        const resolver = entry.resolve.near
        if (resolver?.kind !== "near-walletid") continue
        const match = nearWallets.find(
          (w) => w.manifest.id === resolver.walletId
        )
        if (!match) continue
        coveredWalletIds.add(resolver.walletId)
        coveredNames.add(entry.name.toLowerCase())
        out.push({
          key: entry.id,
          testId: `wallet-tile-${entry.id}`,
          name: entry.name,
          icon: entry.icon ?? match.manifest.icon ?? undefined,
          // NEAR connector wallets are sandbox-loadable; "installation" doesn't
          // apply the same way as browser extensions.
          status: "available",
          onConnect: () =>
            safeConnect(entry.id, () => connectViaResolver(entry, "near", ctx)),
        })
      }

      for (const w of nearWallets) {
        const walletId = w.manifest.id
        if (coveredWalletIds.has(walletId)) continue
        if (coveredNames.has(w.manifest.name.toLowerCase())) continue
        out.push({
          key: `near:${walletId}`,
          testId: `wallet-tile-near-${walletId}`,
          name: w.manifest.name,
          icon:
            walletIconForConnector({
              chain: "near",
              id: walletId,
              name: w.manifest.name,
            }) ??
            w.manifest.icon ??
            undefined,
          status: "available",
          onConnect: () =>
            safeConnect(`near:${walletId}`, () =>
              signIn({ id: ChainType.Near, walletId })
            ),
        })
      }
    } else if (chain === "stellar") {
      const coveredWalletIds = new Set<string>()

      for (const entry of registryEntries) {
        const resolver = entry.resolve.stellar
        if (resolver?.kind !== "stellar-walletid") continue
        const match = stellarWallets.find((w) => w.id === resolver.walletId)
        if (!match) continue
        coveredWalletIds.add(resolver.walletId)
        coveredNames.add(entry.name.toLowerCase())
        const status: WalletStatus = STELLAR_UNRELIABLE_AVAILABILITY.has(
          match.id
        )
          ? "available"
          : match.isAvailable
            ? "installed"
            : "not-installed"
        out.push({
          key: entry.id,
          testId: `wallet-tile-${entry.id}`,
          name: entry.name,
          icon: entry.icon ?? match.icon ?? undefined,
          status,
          onConnect: () =>
            safeConnect(entry.id, () =>
              connectViaResolver(entry, "stellar", ctx)
            ),
        })
      }

      for (const w of stellarWallets) {
        if (coveredWalletIds.has(w.id)) continue
        if (coveredNames.has(w.name.toLowerCase())) continue
        const status: WalletStatus = STELLAR_UNRELIABLE_AVAILABILITY.has(w.id)
          ? "available"
          : w.isAvailable
            ? "installed"
            : "not-installed"
        out.push({
          key: `stellar:${w.id}`,
          testId: `wallet-tile-stellar-${w.id}`,
          name: w.name,
          icon:
            walletIconForConnector({
              chain: "stellar",
              id: w.id,
              name: w.name,
            }) ??
            w.icon ??
            undefined,
          status,
          onConnect: () =>
            safeConnect(`stellar:${w.id}`, () =>
              signIn({ id: ChainType.Stellar, walletId: w.id })
            ),
        })
      }
    }

    return out.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status])
  }, [
    chain,
    wagmiConnectors,
    solanaWallet.wallets,
    nearConnector,
    stellarWallets,
    signIn,
    safeConnect,
  ])

  const availableWallets = wallets.filter((w) => w.status !== "not-installed")
  const notInstalled = wallets.filter((w) => w.status === "not-installed")

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleClose}
      onCloseAnimationEnd={onCloseAnimationEnd}
      title={chain ? `Choose your ${CHAIN_LABELS[chain]} wallet` : undefined}
      bgClassName="bg-gray-50 dark:bg-gray-850"
    >
      <div className="grid grid-cols-2 gap-2 pt-2">
        <h3 className="sr-only">Available wallets</h3>
        {availableWallets.map((wallet) => (
          <WalletTile
            key={wallet.key}
            wallet={wallet}
            isConnecting={connectingKey === wallet.key}
            disabled={connectingKey != null && connectingKey !== wallet.key}
          />
        ))}
      </div>
      {notInstalled.length > 0 && (
        <div className="mt-6 border-gray-200 border-t pt-6 dark:border-white/10">
          <h3 className="font-semibold text-base/4 text-gray-900 dark:text-white">
            Not installed
          </h3>
          <div className="grid grid-cols-2 gap-2 pt-5">
            {notInstalled.map((wallet) => (
              <WalletTile
                key={wallet.key}
                wallet={wallet}
                isConnecting={false}
                disabled={connectingKey != null}
              />
            ))}
          </div>
        </div>
      )}
    </Dialog>
  )
}

function solanaReadyStateToStatus(
  state: WalletReadyState,
  adapterName: string
): WalletStatus {
  switch (state) {
    case WalletReadyState.Installed:
      return "installed"
    case WalletReadyState.Loadable:
      return SOLANA_EXTENSION_FIRST_ADAPTERS.has(adapterName)
        ? "not-installed"
        : "available"
    case WalletReadyState.NotDetected:
    case WalletReadyState.Unsupported:
      return "not-installed"
  }
}

function WalletTile({
  wallet,
  isConnecting,
  disabled,
}: {
  wallet: DisplayWallet
  isConnecting: boolean
  disabled: boolean
}) {
  const { name, icon, status, onConnect, testId } = wallet

  return (
    <WalletButton
      name={name}
      icon={icon}
      dataTestId={testId}
      onClick={() => onConnect()}
      disabled={status === "not-installed" || disabled || isConnecting}
      badge={
        isConnecting ? (
          <Spinner size="sm" className="text-gray-500 dark:text-gray-400" />
        ) : status === "installed" ? (
          <Badge variant="success" hideIcon>
            Installed
          </Badge>
        ) : null
      }
    />
  )
}
